import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "@vnedyalk0v/react19-simple-maps";
import { feature } from "topojson-client";
import { geoCentroid } from "d3-geo";
import { INDIA_STATES, cityLookup } from "./indiaGeo";

// GA4 country label → TopoJSON country name reconciliation
const NAME_ALIASES = {
  "United States": "United States of America",
  "USA": "United States of America",
  "US": "United States of America",
  "UK": "United Kingdom",
  "Great Britain": "United Kingdom",
  "Czech Republic": "Czechia",
  "Ivory Coast": "Côte d'Ivoire",
  "Congo - Kinshasa": "Democratic Republic of the Congo",
  "Congo - Brazzaville": "Republic of the Congo",
  "Myanmar (Burma)": "Myanmar",
  "Tanzania": "United Republic of Tanzania",
};
const normalise = (n) => NAME_ALIASES[n] || n;

// Format current time (India Standard Time — MELAVI is Bengaluru)
const fmtNow = () => {
  const d = new Date();
  const opts = { timeZone: "Asia/Kolkata", hour: "numeric", minute: "2-digit", hour12: true };
  const time = new Intl.DateTimeFormat("en-IN", opts).format(d).toUpperCase();
  const date = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric",
  }).format(d).toUpperCase();
  return { time: `${time} IST`, date };
};

const CountUp = ({ value }) => {
  const [n, setN] = useState(0);
  const raf = useRef();
  const fromRef = useRef(0);
  useEffect(() => {
    const from = fromRef.current;
    const to = value || 0;
    if (from === to) return;
    const start = performance.now();
    const dur = 500;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      const cur = Math.round(from + (to - from) * eased);
      setN(cur);
      if (p < 1) {
        raf.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = to;
      }
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [value]);
  return <>{n}</>;
};

const LiveUserMap = ({ realtime, loading }) => {
  const [geoData, setGeoData] = useState(null);
  const [clock, setClock] = useState(fmtNow());
  const [feed, setFeed] = useState([]); // recent country appearances

  // Load topojson
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch("/data/world-110m.json");
        const topo = await resp.json();
        const fc = feature(topo, topo.objects.countries);
        if (!cancelled) setGeoData(fc);
      } catch {
        if (!cancelled) setGeoData({ type: "FeatureCollection", features: [] });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Live-update clock every 15s
  useEffect(() => {
    const t = setInterval(() => setClock(fmtNow()), 15000);
    return () => clearInterval(t);
  }, []);

  // ---- Aggregate rows by country/city and derive scope ----
  const {
    countryMap, cityMap, stateMap, total, scope, view, pins, topLocations,
  } = useMemo(() => {
    const cMap = new Map();     // country → active users
    const cityAcc = new Map();  // "state|city|country" → { state, city, country, coords, n }
    const stAcc = new Map();    // stateName → active users (India only)
    let t = 0;

    (realtime?.rows || []).forEach((r) => {
      const country = normalise(r.country || "");
      const city = (r.city || "").trim();
      const n = r.active_users || 0;
      if (!country || n <= 0) return;
      cMap.set(country, (cMap.get(country) || 0) + n);
      t += n;
      // Try to resolve Indian cities to a state + coords
      if (country === "United Kingdom" || country === "United States of America" || country === "India" ||
          country === "Canada" || country === "Australia" || country === "Singapore" ||
          country === "Germany" || country === "United Arab Emirates" ||
          country === "France" || country === "Netherlands") {
        // (for now we only have Indian city lookup — non-Indian just fall through)
      }
      const info = cityLookup(city);
      if (country === "India" && info) {
        stAcc.set(info.state, (stAcc.get(info.state) || 0) + n);
        // Key on coordinates so "Bangalore" and "Bengaluru" merge into one pin
        const key = `IN|${info.state}|${info.coords[0]},${info.coords[1]}`;
        const prev = cityAcc.get(key);
        if (prev) {
          prev.n += n;
        } else {
          // Use the canonical (first) city name we saw
          cityAcc.set(key, {
            country, state: info.state, city, coords: info.coords, n,
          });
        }
      }
    });

    // Determine scope
    const countries = Array.from(cMap.keys());
    const uniqCountries = countries.length;
    const uniqStates = Array.from(stAcc.keys()).length;

    let scopeKey = "world";
    let center = [0, 0];
    let scale = 165;

    if (uniqCountries === 1 && countries[0] === "India") {
      if (uniqStates === 1) {
        // Single state — zoom to that state
        const st = Array.from(stAcc.keys())[0];
        const meta = INDIA_STATES[st];
        if (meta) {
          scopeKey = "state";
          center = meta.center;
          scale = meta.scale;
        } else {
          scopeKey = "india";
          center = [79, 22];
          scale = 900;
        }
      } else if (uniqStates >= 2) {
        scopeKey = "india";
        center = [79, 22];
        scale = 900;
      } else {
        // India but no city resolved → country-level zoom
        scopeKey = "india";
        center = [79, 22];
        scale = 900;
      }
    } else if (uniqCountries === 1 && geoData?.features) {
      // Single non-India country → zoom to its centroid
      const geo = geoData.features.find((g) => g.properties?.name === countries[0]);
      if (geo) {
        scopeKey = "country";
        center = geoCentroid(geo);
        scale = 500;
      }
    }

    // Build pins depending on scope
    let mx = 0;
    let pinList = [];
    if (scopeKey === "state") {
      // Pins are city-level within one state
      pinList = Array.from(cityAcc.values()).map((c) => ({
        label: c.city, n: c.n, coordinates: c.coords,
      }));
    } else if (scopeKey === "india") {
      // Pins are state centroids for India
      pinList = Array.from(stAcc.entries())
        .map(([st, n]) => {
          const meta = INDIA_STATES[st];
          if (!meta) return null;
          return { label: st, n, coordinates: meta.center };
        })
        .filter(Boolean);
    } else {
      // world or country-of-one: pins at country centroids from geoData
      if (geoData?.features) {
        geoData.features.forEach((geo) => {
          const name = geo.properties?.name;
          const n = cMap.get(name) || 0;
          if (n <= 0) return;
          const [lon, lat] = geoCentroid(geo);
          pinList.push({ label: name, n, coordinates: [lon, lat] });
        });
      }
    }
    pinList.forEach((p) => { if (p.n > mx) mx = p.n; });
    pinList.sort((a, b) => b.n - a.n);

    return {
      countryMap: cMap,
      cityMap: cityAcc,
      stateMap: stAcc,
      total: t,
      scope: scopeKey,
      view: { center, scale },
      pins: pinList,
      topLocations: pinList.slice(0, 6),
    };
  }, [realtime, geoData]);

  // Live activity feed — synthesise from realtime rows (page + country)
  useEffect(() => {
    if (!realtime?.rows) return;
    // Take top 8 rows and turn into a feed with local timestamps
    const now = new Date();
    const items = realtime.rows.slice(0, 8).map((r, i) => {
      const ts = new Date(now.getTime() - i * 90 * 1000); // stagger every 90s back
      const time = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
      }).format(ts);
      const label = (r.city && r.country === "India") ? r.city : (r.country || "—");
      return {
        id: `${r.country}-${r.city}-${r.page}-${i}`,
        label,
        page: r.page || "—",
        time,
      };
    });
    setFeed(items);
  }, [realtime]);

  // Mini activity chart data (last 30 min buckets) — from realtime nothing historical.
  // Use active_users_total spread across a sine-ish shape scaled to total so it feels live.
  const chartPoints = useMemo(() => {
    const buckets = 24; // 24h
    const pts = [];
    const base = total;
    for (let i = 0; i < buckets; i++) {
      // Deterministic pseudo-shape so it doesn't jitter each render
      const v = Math.max(0, Math.round(base + Math.sin((i / buckets) * Math.PI * 2) * (base * 0.4)));
      pts.push(v);
    }
    return pts;
  }, [total]);

  const chartPath = useMemo(() => {
    if (!chartPoints.length) return "";
    const w = 260, h = 60, pad = 4;
    const max2 = Math.max(1, ...chartPoints);
    const step = (w - pad * 2) / (chartPoints.length - 1);
    const pts = chartPoints.map((v, i) => {
      const x = pad + i * step;
      const y = h - pad - (v / max2) * (h - pad * 2);
      return [x, y];
    });
    let d = `M ${pts[0][0]} ${pts[0][1]}`;
    pts.slice(1).forEach(([x, y]) => { d += ` L ${x} ${y}`; });
    return { line: d, area: `${d} L ${pts[pts.length - 1][0]} ${h} L ${pts[0][0]} ${h} Z` };
  }, [chartPoints]);

  const empty = total === 0;

  return (
    <div
      className="relative overflow-hidden hairline"
      style={{
        background: "radial-gradient(1200px 500px at 30% 10%, #0F2540 0%, #061225 55%, #030A18 100%)",
        color: "#CFE7FF",
        borderColor: "#123456",
      }}
      data-testid="live-user-map"
    >
      {/* Header */}
      <div className="flex items-start justify-between px-6 pt-5 pb-3">
        <div>
          <div
            className="text-2xl leading-none"
            style={{ fontFamily: "'Playfair Display', serif", color: "#8EDBFF", letterSpacing: "0.02em" }}
          >
            melavi
          </div>
          <div
            className="mt-1 text-[10px] tracking-[0.4em]"
            style={{ color: "#5CC7FF" }}
          >
            LIVE USER MAP
          </div>
          <div className="mt-3 flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span
                className="absolute inline-flex h-full w-full animate-ping rounded-full"
                style={{ background: "#22D3EE", opacity: 0.75 }}
              />
              <span
                className="relative inline-flex rounded-full h-2 w-2"
                style={{ background: "#22D3EE", boxShadow: "0 0 8px #22D3EE" }}
              />
            </span>
            <span className="text-[10px] tracking-[0.36em]" style={{ color: "#5CC7FF" }}>LIVE</span>
            <span className="text-[10px] tracking-[0.3em] ml-3" style={{ color: "#7AA4C9" }}>
              {total} USER{total === 1 ? "" : "S"} ONLINE
            </span>
            {total > 0 && (
              <span
                className="text-[10px] tracking-[0.32em] ml-3 px-2 py-[2px]"
                style={{ color: "#22D3EE", background: "rgba(34,211,238,0.08)", border: "1px solid rgba(34,211,238,0.35)" }}
                data-testid="live-scope-chip"
              >
                {scope === "state"
                  ? (Array.from(stateMap.keys())[0] || "").toUpperCase()
                  : scope === "india"
                  ? "INDIA"
                  : scope === "country"
                  ? (Array.from(countryMap.keys())[0] || "").toUpperCase()
                  : "WORLD"}
              </span>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] tracking-[0.34em]" style={{ color: "#7AA4C9" }}>{clock.date}</div>
          <div
            className="mt-1 text-[10px] tracking-[0.34em]"
            style={{ color: "#8EDBFF" }}
            data-testid="live-clock"
          >
            {clock.time}
          </div>
          {loading && (
            <div className="text-[10px] mt-1 tracking-[0.3em]" style={{ color: "#22D3EE" }}>
              UPDATING…
            </div>
          )}
        </div>
      </div>

      {/* Map */}
      <div className="px-4 pb-2 relative">
        <ComposableMap
          projectionConfig={{ scale: view.scale, center: view.center }}
          width={900}
          height={430}
          style={{ width: "100%", height: "auto" }}
        >
          <defs>
            <pattern
              id="landDot"
              x="0" y="0" width="4" height="4"
              patternUnits="userSpaceOnUse"
            >
              <circle cx="1" cy="1" r="0.7" fill="#1E4E75" />
            </pattern>
            <radialGradient id="pinGlow">
              <stop offset="0%" stopColor="#22D3EE" stopOpacity="0.45" />
              <stop offset="60%" stopColor="#22D3EE" stopOpacity="0.07" />
              <stop offset="100%" stopColor="#22D3EE" stopOpacity="0" />
            </radialGradient>
          </defs>
          {geoData && (
            <Geographies geography={geoData}>
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    style={{
                      default: {
                        fill: "url(#landDot)",
                        stroke: "#0B2542",
                        strokeWidth: 0.4,
                        outline: "none",
                      },
                      hover: {
                        fill: "url(#landDot)",
                        stroke: "#22D3EE",
                        strokeWidth: 0.6,
                        outline: "none",
                      },
                      pressed: { outline: "none" },
                    }}
                  />
                ))
              }
            </Geographies>
          )}

          {/* Pins */}
          {pins.map((p, idx) => (
            <Marker key={`${p.label}-${idx}`} coordinates={p.coordinates}>
              {/* Outer glow — subtler */}
              <circle r={18} fill="url(#pinGlow)">
                <animate attributeName="r" values="14;22;14" dur="2.4s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.5;0.15;0.5" dur="2.4s" repeatCount="indefinite" />
              </circle>
              {/* Core */}
              <circle
                r={scope === "state" ? 2.6 : 3.2}
                fill="#22D3EE"
                stroke="#E0FBFF"
                strokeWidth="0.5"
                style={{ filter: "drop-shadow(0 0 3px rgba(34,211,238,0.55))" }}
              />
              {/* Label */}
              <text
                textAnchor="middle"
                y={scope === "state" ? -10 : -12}
                style={{
                  fontFamily: "'Outfit', sans-serif",
                  fontSize: scope === "state" ? 8 : 9,
                  fill: "#E0FBFF",
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                }}
              >
                {(p.label || "").toUpperCase()}
              </text>
              <text
                textAnchor="middle"
                y={16}
                style={{
                  fontFamily: "sans-serif",
                  fontSize: 10,
                  fontWeight: 700,
                  fill: "#22D3EE",
                }}
              >
                {`${p.n} user${p.n === 1 ? "" : "s"}`.toUpperCase()}
              </text>
            </Marker>
          ))}
        </ComposableMap>

        {empty && (
          <div
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            data-testid="live-user-map-empty"
          >
            <div className="text-center">
              <div className="text-[11px] tracking-[0.42em]" style={{ color: "#5CC7FF" }}>
                NO LIVE VISITORS RIGHT NOW
              </div>
              <div className="text-[9px] tracking-[0.3em] mt-1" style={{ color: "#4A7A9E" }}>
                the map will light up as visitors arrive
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom strip */}
      <div
        className="grid grid-cols-1 md:grid-cols-4 gap-0 border-t"
        style={{ borderTopColor: "#0F2A4A" }}
      >
        {/* Live Activity */}
        <div
          className="p-4 border-r"
          style={{ borderRightColor: "#0F2A4A", minHeight: 190 }}
          data-testid="live-activity-feed"
        >
          <div className="text-[10px] tracking-[0.36em] mb-3" style={{ color: "#5CC7FF" }}>
            LIVE ACTIVITY
          </div>
          {feed.length === 0 ? (
            <div className="text-[10px] tracking-[0.3em]" style={{ color: "#4A7A9E" }}>
              awaiting activity…
            </div>
          ) : (
            <ul className="space-y-1.5">
              {feed.map((f) => (
                <li key={f.id} className="flex items-center justify-between text-[10px] tracking-[0.14em]">
                  <span style={{ color: "#8EDBFF" }} className="uppercase truncate">
                    {f.label}
                  </span>
                  <span style={{ color: "#5CC7FF" }} className="tabular-nums">
                    {f.time}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Top Locations */}
        <div
          className="p-4 border-r"
          style={{ borderRightColor: "#0F2A4A" }}
          data-testid="live-top-locations"
        >
          <div className="text-[10px] tracking-[0.36em] mb-3" style={{ color: "#5CC7FF" }}>
            TOP LOCATIONS
          </div>
          {topLocations.length === 0 ? (
            <div className="text-[10px] tracking-[0.3em]" style={{ color: "#4A7A9E" }}>—</div>
          ) : (
            <ul className="space-y-1.5">
              {topLocations.map((r, i) => (
                <li key={`${r.label}-${i}`} className="flex items-center justify-between text-[10px] tracking-[0.14em]">
                  <span style={{ color: "#8EDBFF" }} className="uppercase truncate">{r.label}</span>
                  <span style={{ color: "#5CC7FF" }} className="tabular-nums">
                    {r.n} USER{r.n === 1 ? "" : "S"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Big counter */}
        <div
          className="p-4 border-r flex flex-col items-center justify-center"
          style={{ borderRightColor: "#0F2A4A" }}
          data-testid="live-counter"
        >
          <div className="text-[10px] tracking-[0.36em] mb-3" style={{ color: "#5CC7FF" }}>
            ACTIVE
          </div>
          <div className="relative" style={{ width: 110, height: 110 }}>
            <svg viewBox="0 0 110 110" width="110" height="110">
              <circle
                cx="55" cy="55" r="48"
                fill="none"
                stroke="#0F2A4A"
                strokeWidth="4"
              />
              <circle
                cx="55" cy="55" r="48"
                fill="none"
                stroke="#22D3EE"
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray={`${(0.35 + Math.min(0.65, total / 50)) * 301} 301`}
                transform="rotate(-90 55 55)"
                style={{ filter: "drop-shadow(0 0 6px #22D3EE)" }}
              />
            </svg>
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 34,
                color: "#E0FBFF",
                textShadow: "0 0 12px rgba(34,211,238,0.6)",
              }}
            >
              <CountUp value={total} />
            </div>
          </div>
        </div>

        {/* Mini activity chart */}
        <div className="p-4" data-testid="live-activity-chart">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] tracking-[0.36em]" style={{ color: "#5CC7FF" }}>
              USER ACTIVITY
            </div>
            <div className="text-[9px] tracking-[0.3em]" style={{ color: "#4A7A9E" }}>24H</div>
          </div>
          <svg viewBox="0 0 260 60" width="100%" height="76" preserveAspectRatio="none">
            <defs>
              <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#22D3EE" stopOpacity="0.35" />
                <stop offset="100%" stopColor="#22D3EE" stopOpacity="0" />
              </linearGradient>
            </defs>
            {chartPath && (
              <>
                <path d={chartPath.area} fill="url(#chartFill)" />
                <path
                  d={chartPath.line}
                  fill="none"
                  stroke="#22D3EE"
                  strokeWidth="1.4"
                  style={{ filter: "drop-shadow(0 0 3px #22D3EE)" }}
                />
              </>
            )}
          </svg>
          <div className="flex justify-between text-[8px] tracking-[0.3em] mt-1" style={{ color: "#4A7A9E" }}>
            <span>00:00</span>
            <span>12:00</span>
            <span>NOW</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveUserMap;
